import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Lock } from "lucide-react";

export const NAV_LINKS = [
  { label: "Accueil", to: "/" },
  { label: "Qui sommes-nous", to: "/qui-sommes-nous" },
  { label: "Stage Payet", to: "/stage-payet" },
  { label: "Stage Pro / Semi-Pro", to: "/stage-pro" },
  { label: "Matchs amicaux", to: "/matchs-amicaux" },
  { label: "Boutique", to: "/boutique" },
  { label: "Équipementier sportif", to: "/equipementier" },
  { label: "Sponsors / Partenaires", to: "/sponsors" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/5" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
          <Link to="/" data-testid="header-logo" className="flex items-center group">
            <img
              src="/logo-3m.png"
              alt="3M Match Pro — Organisateur d'événements sportifs"
              className="h-10 md:h-12 w-auto object-contain group-hover:opacity-90 transition-opacity"
            />
          </Link>

          <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-5">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to === "/" ? "accueil" : link.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 border border-white/20 text-white/70 text-[10px] font-bold uppercase tracking-[0.14em] rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <Lock size={11} /> Admin
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            className="lg:hidden relative w-11 h-11 flex items-center justify-center border border-white/15 text-white hover:border-gold hover:text-gold transition-colors"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-[#050505] lg:hidden flex flex-col pt-24 px-8 overflow-y-auto"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.to === "/" ? "accueil" : link.to.slice(1)}`}
                    className={({ isActive }) =>
                      `block font-display text-4xl sm:text-5xl uppercase tracking-wide py-2.5 transition-colors ${
                        isActive ? "text-gold" : "text-white hover:text-gold"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-10 mb-10"
            >
              <Link
                to="/admin"
                data-testid="mobile-nav-link-admin"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white/70 text-xs font-bold uppercase tracking-[0.2em] rounded-full hover:border-gold hover:text-gold transition-colors"
              >
                <Lock size={13} /> Admin
              </Link>
              <p className="mt-6 text-white/40 text-xs uppercase tracking-[0.3em]">
                Stages de football — Matchs amicaux pro & semi-pro
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
